import React, { useState, useEffect } from 'react'
import styled, { css, SimpleInterpolation } from 'styled-components'
import { useInView } from 'react-intersection-observer'

// Styling
import { ease, enterOnScroll } from '../../styles/animations'

interface IProps {
  text: string[]
  delay?: number
  textStyles?: SimpleInterpolation
  className?: string
}

// Setup
const ANIMATION_DELAY = 0.5
const ANIMATION_DURATION = 1

const AnimatedTextScrollWrapper: React.FC<IProps> = ({
  text,
  delay = ANIMATION_DELAY,
  textStyles,
  className,
}) => {
  // Convert to ms
  const timeoutDuration = (delay + ANIMATION_DURATION) * 1000

  // Use hooks
  const [isAnimationDone, setisAnimationDone] = useState(false)
  const [ref, inView] = useInView({
    rootMargin: '0px 0px 0px 0px',
    triggerOnce: true,
    threshold: 0,
  })

  useEffect(() => {
    if (inView) {
      const timeout = setTimeout(
        () => setisAnimationDone(true),
        timeoutDuration
      )

      return () => clearTimeout(timeout)
    }

    return
  }, [inView])

  return (
    <>
      {text.map((line, i) => (
        <Wrapper
          key={i}
          ref={ref}
          isAnimationDone={isAnimationDone}
          className={className}
        >
          <Text
            inView={inView}
            delayMultiplier={i}
            delay={delay}
            textStyles={textStyles}
          >
            {line}
          </Text>
        </Wrapper>
      ))}
    </>
  )
}

const Wrapper = styled.div<{ isAnimationDone: boolean }>`
  /**
   * Setting "hidden" will make elements appear from "invisible" containers.
   * Resetting to "visible" will allow the items to use their default overflow styling,
   * as well as show the default focus outline which would otherwise be hidden.
   */
  overflow: ${({ isAnimationDone }) =>
    isAnimationDone ? 'visible' : 'hidden'};
  width: 100%;
`

interface IText {
  inView: boolean
  delayMultiplier: number
  delay: number
  textStyles: SimpleInterpolation
}
const Text = styled.div<IText>`
  ${({ textStyles }) => textStyles && textStyles};
  /* Fixes subpixel calculations */
  transform: translateY(101%);

  ${({ inView, delayMultiplier, delay }) =>
    inView &&
    css`
      animation-name: ${enterOnScroll};
      animation-duration: ${ANIMATION_DURATION}s;
      animation-fill-mode: forwards;
      animation-timing-function: ${ease.animationWrapperCubic};
      animation-delay: ${delayMultiplier * 0.15 + delay}s;
    `}
`

export default AnimatedTextScrollWrapper
